import {
  AgxService,
  ReplayQueueMessageRequestService,
  useGetPoisonQueueMessages,
  useInvalidateUseGetPoisonQueueMessages,
  useInvalidateUseGetPoisonQueues,
  useReplayQueueMessage,
} from '@urbanx/agx.support.client';
import { AgxButton, AgxColumn, AgxRow, Icon_Arrow } from '@urbanx/agx.ui-components2';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PoisonQueueLogs from './PoisonQueueLogs/PoisonQueueLogs';
import PoisonQueueMessageDetails from './PoisonQueueMessageDetails/PoisonQueueMessageDetails';
import PoisonQueueMessageCommonInformation from './PoisonQueueMessageCommonInformation/PoisonQueueMessageCommonInformation';
import SupportBackButton from '../SupportBackButton';

const PoisonQueueMessage = () => {
  const { service, queueName, messageId } = useParams<{
    service: AgxService;
    queueName: string;
    messageId: string;
  }>();

  const { data: queueMessages, isPending: isQueueMessagesPending } = useGetPoisonQueueMessages(
    service!,
    queueName!,
    !!service && !!queueName
  );

  const selectedQueueMessage = useMemo(() => {
    return queueMessages?.find((msg) => msg.id === messageId);
  }, [queueMessages, messageId]);

  const navigate = useNavigate();

  const onBackPressed = useCallback(() => {
    navigate(`../`);
  }, [navigate]);

  const invalidatePoisonQueues = useInvalidateUseGetPoisonQueues();
  const invalidatePoisonQueueMessages = useInvalidateUseGetPoisonQueueMessages();

  const { replayQueueMessage, replayQueueMessagePending } = useReplayQueueMessage({
    onSuccess: async () => {
      console.log('Replay successful');
      await invalidatePoisonQueues();
      await invalidatePoisonQueueMessages(service!, queueName!);
      navigate(`../`);
    },
    onError: () => {
      console.log('Replay failed');
    },
  });

  const commandPending = useMemo(() => {
    return replayQueueMessagePending;
  }, [replayQueueMessagePending]);

  return (
    <AgxColumn fill gap="spacing-4xl">
      <SupportBackButton />

      <AgxButton
        id={'backToPoisonQueueButton'}
        text={'Poison Queue'}
        variant={'naked'}
        leftIcon={<Icon_Arrow direction={'left'} />}
        onClick={onBackPressed}
        disabled={commandPending}
      />

      <AgxButton
        id={'replayMessage'}
        text={'Replay Message'}
        variant={'primary'}
        size="large"
        onClick={() => {
          if (!selectedQueueMessage?.id || !service || !queueName) return;

          replayQueueMessage({
            service: service! as unknown as ReplayQueueMessageRequestService,
            poisonQueueName: queueName!,
            poisonMessageId: selectedQueueMessage.id,
          });
        }}
        disabled={commandPending || !selectedQueueMessage?.id || !service || !queueName}
      />

      <AgxRow gap="spacing-4xl" fill>
        <AgxColumn gap="spacing-4xl" fill>
          <PoisonQueueMessageDetails
            isPending={isQueueMessagesPending}
            queueMessage={selectedQueueMessage}
            queueName={queueName}
            service={service}
          />
          <PoisonQueueMessageCommonInformation
            messageBody={selectedQueueMessage?.messageBodyJson}
          />
        </AgxColumn>
        <PoisonQueueLogs service={service} selectedQueueMessage={selectedQueueMessage} />
      </AgxRow>
    </AgxColumn>
  );
};

export default PoisonQueueMessage;
